import React, { useState, useEffect } from 'react';
import { Modal } from 'reactstrap';
import Select from "react-select";
import Swal from "sweetalert2/dist/sweetalert2.js";

// ModalTaimesAgentField — per-file-field AI config, opened from the file question
// editor (the little robot icon next to "Permitir múltiples archivos").
//
// One modal instance configures the AI for ONE answer_file question:
//   - agent_id + prompt (what the agent should look at)
//   - mapeos: (clave del `datos` de la IA → pregunta destino del formato)
//   - validacion: cancel the upload when `datos.coincide === false`, with a message
//   - "Probar": runs Taimes::Agent.evaluate against a sample file URL so you can see
//     the agent's real `datos` keys and pick them from a datalist when mapping.
//
// Visual style matches the V2 modal system (namespace `taf-`, see
// stylesheets/formatos/taimes_agent_field.scss). Saved per-question via
// PATCH /formatos/taimes_agent_config/:question_id. Replaces the old
// format-wide "IA Archivos" tab (ConfigurationTaimesAgent.jsx).

const emptyConfig = () => ({
    enabled: false,
    agent_id: "",
    prompt: "",
    mapeos: [],
    validacion: { si_no_coincide: "nada", mensaje: "" }
});

const normalizeConfig = (raw) => {
    const c = raw && typeof raw === "object" ? raw : {};
    const v = c.validacion && typeof c.validacion === "object" ? c.validacion : {};
    return {
        enabled: !!c.enabled,
        agent_id: c.agent_id || "",
        prompt: c.prompt || "",
        mapeos: Array.isArray(c.mapeos)
            ? c.mapeos.map(m => ({ clave: (m && m.clave) || "", question_id: (m && (m.question_id != null ? String(m.question_id) : "")) || "" }))
            : [],
        validacion: {
            si_no_coincide: v.si_no_coincide || "nada",
            mensaje: v.mensaje || ""
        }
    };
};

const selectStyles = {
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    control: base => ({ ...base, minHeight: 40, fontSize: '13px' }),
    option: base => ({ ...base, fontSize: '13px' })
};

const ModalTaimesAgentField = ({ modal, toggle, format, question }) => {
    const token = document.querySelector("[name='csrf-token']").content;
    const questionId = question && question.id;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState(emptyConfig());
    const [targetOptions, setTargetOptions] = useState([]);

    // "Probar" state
    const [sampleUrl, setSampleUrl] = useState("");
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null); // { resumen, datos }
    const [datosKeys, setDatosKeys] = useState([]);      // keys surfaced by the last test

    useEffect(() => {
        if (modal && questionId) load();
    }, [modal, questionId]);

    const load = () => {
        setLoading(true);
        setTestResult(null);
        fetch(`/formatos/get_questions/${format.id}`, {
            method: 'GET',
            headers: { "X-CSRF-Token": token, "Content-Type": "application/json" }
        })
            .then(res => res.json())
            .then(data => {
                const register = data.register || [];
                const mine = register.find(q => String(q.id) === String(questionId));
                setConfig(normalizeConfig(mine ? mine.taimes_ai_config : null));
                setTargetOptions(
                    register
                        .filter(q => q.question_type !== "answer_file" && String(q.id) !== String(questionId))
                        .map(q => ({ label: q.question, value: String(q.id), type: q.question_type }))
                );
            })
            .catch(() => Swal.fire({ icon: "error", title: "No se pudieron cargar las preguntas" }))
            .finally(() => setLoading(false));
    };

    const patch = (p) => setConfig(prev => ({ ...prev, ...p }));
    const patchValidacion = (p) => setConfig(prev => ({ ...prev, validacion: { ...prev.validacion, ...p } }));
    const addMapeo = () => setConfig(prev => ({ ...prev, mapeos: [...prev.mapeos, { clave: "", question_id: "" }] }));
    const updateMapeo = (idx, p) => setConfig(prev => ({
        ...prev, mapeos: prev.mapeos.map((m, i) => (i === idx ? { ...m, ...p } : m))
    }));
    const removeMapeo = (idx) => setConfig(prev => ({ ...prev, mapeos: prev.mapeos.filter((_, i) => i !== idx) }));

    const runTest = () => {
        if (!config.agent_id.trim()) {
            Swal.fire({ icon: "warning", title: "Falta el agente", text: "Ingresa el id del agente antes de probar." });
            return;
        }
        if (!sampleUrl.trim()) {
            Swal.fire({ icon: "warning", title: "Falta la URL", text: "Pega la URL de un archivo de muestra." });
            return;
        }
        setTesting(true);
        setTestResult(null);
        fetch(`/formatos/taimes_agent_test`, {
            method: 'POST',
            headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
            body: JSON.stringify({ agent_id: config.agent_id.trim(), prompt: config.prompt, file_url: sampleUrl.trim() })
        })
            .then(res => res.json().then(body => ({ ok: res.ok, body })))
            .then(({ ok, body }) => {
                if (ok && body.success) {
                    const datos = body.datos && typeof body.datos === "object" ? body.datos : {};
                    setTestResult({ resumen: body.resumen || "", datos });
                    setDatosKeys(Object.keys(datos));
                } else {
                    Swal.fire({ icon: "error", title: "No se pudo probar", text: (body && body.error) || "" });
                }
            })
            .catch(() => Swal.fire({ icon: "error", title: "No se pudo probar la IA" }))
            .finally(() => setTesting(false));
    };

    const save = () => {
        if (config.enabled && !config.agent_id.trim()) {
            Swal.fire({ icon: "warning", title: "Falta el agente", text: "Ingresa el id del agente de Taimes." });
            return;
        }
        setSaving(true);
        fetch(`/formatos/taimes_agent_config/${questionId}`, {
            method: 'PATCH',
            headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
            body: JSON.stringify({ taimes_ai_config: config })
        })
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    Swal.fire({ icon: "success", title: "Configuración guardada", timer: 1400, showConfirmButton: false });
                    toggle && toggle();
                } else {
                    Swal.fire({ icon: "error", title: "Error al guardar", text: data.error || "" });
                }
            })
            .catch(() => Swal.fire({ icon: "error", title: "Error al guardar" }))
            .finally(() => setSaving(false));
    };

    const datalistId = `taf-datos-keys-${questionId}`;

    return (
        <Modal isOpen={modal} toggle={toggle} size="lg" backdrop="static"
            className="modal-dialog-centered taf-modal">
            {/* Header */}
            <div className="taf-header">
                <span className="taf-header__icon"><i className="fas fa-robot"></i></span>
                <div className="taf-header__titles">
                    <h5>Configuración de IA</h5>
                    <div className="taf-header__sub">{question ? question.question : ""}</div>
                </div>
                <button type="button" className="taf-header__close" onClick={toggle}>
                    <i className="fas fa-times"></i>
                </button>
            </div>

            {/* Body */}
            <div className="taf-body">
                {loading ? (
                    <div className="text-center py-5">
                        <i className="fas fa-spinner fa-spin fa-2x" style={{ color: '#2563eb' }}></i>
                    </div>
                ) : (
                    <>
                        {/* Master switch */}
                        <div className="taf-card taf-card--switch">
                            <div>
                                <div className="taf-card__title" style={{ fontWeight: 600 }}>Revisar con IA al subir el archivo</div>
                                <div className="taf-card__hint">Al subir un archivo, la IA lo analiza y llena las preguntas mapeadas.</div>
                            </div>
                            <div className="custom-control custom-switch taf-switch">
                                <input type="checkbox" className="custom-control-input" id={`taf-enabled-${questionId}`}
                                    checked={config.enabled} onChange={e => patch({ enabled: e.target.checked })} />
                                <label className="custom-control-label" htmlFor={`taf-enabled-${questionId}`}>
                                    {config.enabled ? "Activa" : "Inactiva"}
                                </label>
                            </div>
                        </div>

                        <div className={config.enabled ? "" : "taf-section-disabled"}>
                            {/* Agent + prompt */}
                            <div className="taf-card">
                                <div className="taf-field">
                                    <label className="taf-label">Agente (agent_id)</label>
                                    <input type="text" className="taf-input taf-input--mono" value={config.agent_id}
                                        onChange={e => patch({ agent_id: e.target.value })}
                                        placeholder="p. ej. a72c0293-0639-4e74-ac94-fffe302037af" />
                                </div>
                                <div className="taf-field">
                                    <label className="taf-label">Prompt (qué revisar)</label>
                                    <textarea className="taf-textarea" value={config.prompt}
                                        onChange={e => patch({ prompt: e.target.value })}
                                        placeholder="p. ej. Identifica si es una llanta y describe su estado." />
                                </div>
                            </div>

                            {/* Probar */}
                            <div className="taf-card taf-card--test">
                                <div className="taf-card__title">
                                    <i className="fas fa-flask"></i>Probar con un archivo de muestra
                                </div>
                                <div className="taf-card__hint">
                                    Corre el agente de verdad y trae las <b>claves</b> del resultado para mapearlas abajo.
                                </div>
                                <div className="taf-test-row">
                                    <input type="text" className="taf-input" value={sampleUrl}
                                        onChange={e => setSampleUrl(e.target.value)}
                                        placeholder="https://.../muestra.jpg" />
                                    <button className="taf-btn taf-btn--soft" onClick={runTest} disabled={testing}>
                                        {testing
                                            ? <><i className="fas fa-spinner fa-spin"></i>Probando…</>
                                            : <><i className="fas fa-play"></i>Probar</>}
                                    </button>
                                </div>
                                {testResult && (
                                    <div className="taf-test-result">
                                        {testResult.resumen && (
                                            <div className="taf-test-result__resumen"><b>Resumen:</b> {testResult.resumen}</div>
                                        )}
                                        <div className="taf-card__hint" style={{ marginBottom: 4 }}>Datos devueltos:</div>
                                        <pre className="taf-datos">{JSON.stringify(testResult.datos, null, 2)}</pre>
                                    </div>
                                )}
                            </div>

                            {/* Mapeo */}
                            <div className="taf-card">
                                <label className="taf-label">Mapeo (dato de la IA → pregunta)</label>
                                <div className="taf-card__hint" style={{ marginBottom: 10 }}>
                                    {datosKeys.length > 0
                                        ? <>Elige una clave devuelta por la IA y la pregunta que se llenará con ella.</>
                                        : <>Escribe la clave del resultado (o usa <b>Probar</b> arriba para verlas) y la pregunta destino.</>}
                                </div>
                                <datalist id={datalistId}>
                                    {datosKeys.map(k => <option key={k} value={k} />)}
                                </datalist>
                                {config.mapeos.length === 0 && (
                                    <div className="taf-empty">Sin mapeos.</div>
                                )}
                                {config.mapeos.map((m, idx) => (
                                    <div className="taf-map-row" key={idx}>
                                        <input type="text" className="taf-input taf-map-row__key" list={datalistId}
                                            value={m.clave}
                                            onChange={e => updateMapeo(idx, { clave: e.target.value })}
                                            placeholder="clave (p. ej. estado)" />
                                        <i className="fas fa-arrow-right taf-map-row__arrow"></i>
                                        <div className="taf-map-row__target">
                                            <Select
                                                options={targetOptions}
                                                value={targetOptions.find(o => o.value === String(m.question_id)) || null}
                                                onChange={opt => updateMapeo(idx, { question_id: opt ? opt.value : "" })}
                                                isClearable placeholder="Pregunta destino..."
                                                classNamePrefix="select"
                                                menuPortalTarget={document.body}
                                                styles={selectStyles}
                                            />
                                        </div>
                                        <button className="taf-map-del" onClick={() => removeMapeo(idx)}>
                                            <i className="fas fa-times"></i>
                                        </button>
                                    </div>
                                ))}
                                <button className="taf-btn taf-btn--ghost taf-btn--sm" onClick={addMapeo}>
                                    <i className="fas fa-plus"></i>Agregar mapeo
                                </button>
                            </div>

                            {/* Validación */}
                            <div className="taf-card" style={{ marginBottom: 0 }}>
                                <div className="custom-control custom-switch taf-switch" style={{ marginBottom: 8 }}>
                                    <input type="checkbox" className="custom-control-input" id={`taf-cancel-${questionId}`}
                                        checked={config.validacion.si_no_coincide === "cancelar"}
                                        onChange={e => patchValidacion({ si_no_coincide: e.target.checked ? "cancelar" : "nada" })} />
                                    <label className="custom-control-label" htmlFor={`taf-cancel-${questionId}`}>
                                        Cancelar la subida si la IA responde <code className="taf-code">datos.coincide = false</code>
                                    </label>
                                </div>
                                {config.validacion.si_no_coincide === "cancelar" && (
                                    <input type="text" className="taf-input" value={config.validacion.mensaje}
                                        onChange={e => patchValidacion({ mensaje: e.target.value })}
                                        placeholder="Mensaje cuando se cancela (p. ej. La foto no corresponde a una llanta)." />
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Footer */}
            <div className="taf-footer">
                <button className="taf-btn taf-btn--ghost" onClick={toggle} disabled={saving}>Cancelar</button>
                <button className="taf-btn taf-btn--primary" onClick={save} disabled={saving || loading}>
                    {saving
                        ? <><i className="fas fa-spinner fa-spin"></i>Guardando…</>
                        : <><i className="fas fa-save"></i>Guardar</>}
                </button>
            </div>
        </Modal>
    );
};

export default ModalTaimesAgentField;
